/**
 * Currency helpers — single source of truth for how prices are formatted
 * across the whole app.
 *
 * Contract:
 *   - The supported set of currencies is `src/config/currencies.json`.
 *   - Every consumer should call `formatCurrency(amount, code)` instead of
 *     hard-coding "$" / "USD" so switching a user's currency in Settings
 *     immediately reflects everywhere.
 *   - When the currency code is unknown, missing, or malformed, we fall
 *     back to USD. The backend does the same in `UserService.toDto`, so
 *     the fallback exists only for defence-in-depth / offline previews.
 */

import CURRENCIES from '../config/currencies.json';

/** Fallback code used whenever we can't resolve a real one. */
export const DEFAULT_CURRENCY_CODE = 'USD';

/**
 * All supported currencies (full ISO 4217 catalog).
 *
 * The `active` flag on each entry gates what shows up in the picker in
 * Settings → General. The full list is still used by `getCurrency()` when
 * resolving a stored code, so an account that saved (e.g.) "EUR" before
 * the flag was flipped off still renders "€" instead of falling back to
 * "$" — the picker just won't offer EUR as a new choice.
 */
export const CURRENCIES_LIST = CURRENCIES;

/**
 * Subset of `CURRENCIES_LIST` where `active === true`. This is what the
 * Settings → General dropdown enumerates. To onboard a new currency just
 * flip its `active` flag in `src/config/currencies.json` — no code change
 * needed.
 */
export const ACTIVE_CURRENCIES_LIST = CURRENCIES.filter(
    (c) => c && c.active === true,
);

// Precomputed lookup: uppercase code → currency object. Building this once
// at module load avoids scanning the list on every call from a render loop.
// We index the *full* list (not just active ones) so a persisted currency
// that was later deactivated still resolves to its own symbol.
const _byCode = new Map(
    CURRENCIES.map((c) => [String(c.code).toUpperCase(), c]),
);

/**
 * Is `code` currently active (i.e. offered in the picker)?
 * Useful when you want to gate a code before saving it — e.g. reject a
 * request that tries to persist a deactivated currency.
 *
 * @param {string | null | undefined} code
 * @returns {boolean}
 */
export function isActiveCurrency(code) {
    const normalised = (code || '').toString().trim().toUpperCase();
    const entry = _byCode.get(normalised);
    return !!(entry && entry.active === true);
}

/**
 * Resolve a currency object by code. Returns the USD entry when the code
 * is missing or not in `currencies.json`, so downstream code can rely on
 * the returned object always having `{ name, code, prefix }`.
 *
 * @param {string | null | undefined} code
 * @returns {{ name: string, code: string, prefix: string }}
 */
export function getCurrency(code) {
    const normalised = (code || '').toString().trim().toUpperCase();
    return (
        _byCode.get(normalised) ||
        _byCode.get(DEFAULT_CURRENCY_CODE) ||
        CURRENCIES[0]
    );
}

/**
 * Convenience for “just give me the symbol / prefix” — most components
 * only need this to render `${symbol}${price}`.
 *
 * @param {string | null | undefined} code
 * @returns {string}
 */
export function getCurrencySymbol(code) {
    return getCurrency(code).prefix;
}

/**
 * Format a numeric amount into a localised currency string.
 *
 * Uses `Intl.NumberFormat` when the browser recognises the currency code,
 * so US and PKR-style locale grouping / decimals come for free. Falls back
 * to `<prefix><amount>.<toFixed>` for unrecognised codes.
 *
 * @param {number | string | null | undefined} amount
 * @param {string | null | undefined} code
 * @param {{
 *   minimumFractionDigits?: number,
 *   maximumFractionDigits?: number,
 *   showSymbol?: boolean,
 * }} [opts]
 * @returns {string}
 */
export function formatCurrency(amount, code, opts = {}) {
    const currency = getCurrency(code);
    const {
        minimumFractionDigits = 2,
        maximumFractionDigits = 2,
        showSymbol = true,
    } = opts;

    // Normalise the numeric value. Empty strings / null / NaN all render
    // as "0" so we never emit "$NaN" into a UI.
    const raw =
        typeof amount === 'number'
            ? amount
            : parseFloat(String(amount ?? '').replace(/[^\d.-]/g, ''));
    const value = Number.isFinite(raw) ? raw : 0;

    // Prefer Intl for real ISO codes so we get proper thousands
    // separators + non-breaking spaces per-locale.
    try {
        const formatted = new Intl.NumberFormat('en-US', {
            style: showSymbol ? 'currency' : 'decimal',
            currency: showSymbol ? currency.code : undefined,
            minimumFractionDigits,
            maximumFractionDigits,
            currencyDisplay: 'narrowSymbol',
        }).format(value);
        // Intl uses the ISO symbol (e.g. "PKR" or "Rs" depending on the
        // browser). Force our chosen prefix so the frontend always matches
        // `currencies.json` — otherwise a user picking PKR could see
        // "PKR 100.00" in Chrome and "Rs 100.00" in Firefox.
        if (showSymbol) {
            const localSym = currency.prefix;
            // Strip any leading currency chars/spaces the browser produced
            // and re-attach our canonical prefix with a thin space.
            const numeric = formatted.replace(/^[^\d-]*/, '').trim();
            return `${localSym}${numeric.startsWith(' ') ? '' : ''}${numeric}`;
        }
        return formatted;
    } catch (_) {
        // Some currency codes (e.g. new custom ones added to
        // currencies.json) may not be known to Intl — fall through.
    }

    const fixed = value.toFixed(maximumFractionDigits);
    return showSymbol ? `${currency.prefix}${fixed}` : fixed;
}

/**
 * Small helper for components that receive the raw user object and just
 * want the active currency code. Centralises the "user.currency || USD"
 * pattern so we don't sprinkle it across dozens of files.
 *
 * @param {{ currency?: string | null } | null | undefined} user
 * @returns {string}
 */
export function resolveUserCurrency(user) {
    return (user && user.currency) || DEFAULT_CURRENCY_CODE;
}

/**
 * Rewrites the currency prefix inside a user-authored price HTML string
 * so it matches the settings-selected currency.
 *
 * Solo profiles let the creator type the price into a contentEditable
 * div, which saves the raw HTML (fonts, styling, and whatever prefix they
 * typed — usually "$"). When the account currency changes, we still want
 * the *symbol* to reflect the new picker, but keep the surrounding
 * `<span>`s / font styles the user chose. This function:
 *   1. strips any leading currency-ish characters from the visible text,
 *   2. re-injects the active symbol via the same node, so a "$99.99"
 *      typed inside `<span style="font-family:Serif">$99.99</span>`
 *      becomes `<span style="font-family:Serif">Rs99.99</span>`.
 *
 * Falls back to a plain `<symbol><amount>` render when the HTML has no
 * numeric text.
 *
 * @param {string | null | undefined} html - the stored priceHtml
 * @param {string | null | undefined} rawPrice - fallback plain price
 * @param {string | null | undefined} code - ISO 4217 code to force
 * @returns {string}
 */
export function rewritePriceHtml(html, rawPrice, code) {
    const currency = getCurrency(code);
    const symbol = currency.prefix;
    const source =
        typeof html === 'string' && html.trim() ? html : (rawPrice || '');
    if (!source) return `${symbol}0.00`;
    // Strip anything that isn't a digit, decimal point, or minus from the
    // *text* only — HTML tags stay. We rebuild the visible text by walking
    // characters and preserving tags, then prepend the new symbol.
    let out = '';
    let inTag = false;
    let numericSeen = false;
    let symbolInjected = false;
    for (let i = 0; i < source.length; i++) {
        const ch = source[i];
        if (ch === '<') { inTag = true; out += ch; continue; }
        if (ch === '>') { inTag = false; out += ch; continue; }
        if (inTag) { out += ch; continue; }
        // A digit or dot / minus is real numeric content — inject the
        // symbol just before the first one we see and copy digits through.
        if (/[0-9.\-]/.test(ch)) {
            if (!symbolInjected) { out += symbol; symbolInjected = true; }
            out += ch;
            numericSeen = true;
            continue;
        }
        // Whitespace inside the visible text (e.g. "Rs 100") is fine to
        // keep once we've already injected the symbol; before that, skip
        // it so we don't emit "$ Rs100.00".
        if (/\s/.test(ch) && symbolInjected) { out += ch; continue; }
        // Anything else (letters, other currency chars, HTML entities the
        // browser will decode later) is dropped from the visible portion.
    }
    if (!numericSeen) {
        // No number in the HTML — fall back to raw price if present.
        return `${symbol}${(parseFloat(String(rawPrice || '0').replace(/[^0-9.\-]/g, '')) || 0).toFixed(2)}`;
    }
    return out;
}
